import React, { useContext, useMemo, useState } from 'react'
import { useEffect } from 'react'
import { ArrowForwardIcon } from '@chakra-ui/icons'
import { HStack, VStack } from '@chakra-ui/react'
import { Steps, Step, useSteps } from 'chakra-ui-steps'
import { EBreakPoint } from 'enums/theme'
import useBreakPoint from 'hooks/useBreakPoint'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import { useFormContext } from 'react-hook-form'
import { handleError } from 'API/error'
import { IApplicationFormValues } from 'interfaces/application'
import { getValidArray } from 'utils/common'
import { ApplicationContext } from '../../applicationForm.context'
import { BottomBackButton, BottomButton } from '../../applicationFormModalV2.styles'
import { DESKTOP_CONTAINER_WIDTH, EApplicationStepBar } from '../../constant'
import { CompleteScreeningContext } from './completeScreening.context'
import { ECompleteScreeningSteps, getCompleteScreeningSteps } from './utils'
import { ICompleteScreeningStep } from './utils'

const CompleteScreening = () => {
  const {
    isLoading,
    isBackLoading,
    setIsBackLoading,
    setIsLoading,
    setStep: setSideBarStep,
    setCompleteScreeningStep,
    completeScreeningStep,
    setIsDriverLicenseVerified
  } = useContext(ApplicationContext)
  const { nextStep, activeStep, prevStep: previousStep, setStep } = useSteps({ initialStep: 0 })
  const { houseApplicationStore, userBackgroundInformationStore } = useStores()
  const { isPaymentExpired } = houseApplicationStore
  const { userBackgroundInformation } = userBackgroundInformationStore
  const isDesktop: boolean = useBreakPoint(EBreakPoint.LG)
  const [isDriverLicenses, setIsDriverLicenses] = useState<boolean>(false)
  const { getValues } = useFormContext()
  const formValue: IApplicationFormValues = getValues()
  const paystubFiles: string[] = getValidArray(userBackgroundInformation?.paystubFiles)
  const steps: ICompleteScreeningStep[] = useMemo(() => {
    return getCompleteScreeningSteps(getValidArray(paystubFiles).length > 0 ? paystubFiles : [])
  }, [handleNextStep, handlePreviousStep])
  const isUsingStepButton: boolean = [
    ECompleteScreeningSteps.PAYMENT_INFORMATION,
    ECompleteScreeningSteps.PAYSTUB_UPLOADER
  ].includes(activeStep)
  function handleNextStep(): void {
    if (activeStep === ECompleteScreeningSteps.DRIVER_LICENSE) {
      if (isDriverLicenses) {
        typeof setIsDriverLicenseVerified === 'function' && setIsDriverLicenseVerified(true)
        typeof setCompleteScreeningStep === 'function' &&
          setCompleteScreeningStep(ECompleteScreeningSteps.PAYSTUB_UPLOADER)
      }
    }
    nextStep()
  }

  async function handlePreviousStep(): Promise<void> {
    typeof setIsBackLoading === 'function' && setIsBackLoading(true)
    try {
      if (activeStep === ECompleteScreeningSteps.PAYMENT_INFORMATION && isPaymentExpired) {
        setSideBarStep(EApplicationStepBar.SHARE_YOUR_DEETS)
        return
      }

      if (activeStep === ECompleteScreeningSteps.DRIVER_LICENSE && !isPaymentExpired) {
        setSideBarStep(EApplicationStepBar.SHARE_YOUR_DEETS)
        return
      }
      previousStep()
    } catch (error) {
      handleError(error as Error, 'components/pages/DetailPage/components/ApplicationFormModalV2', 'handlePreviousStep')
    } finally {
      typeof setIsLoading === 'function' && setIsLoading(false)
      typeof setIsBackLoading === 'function' && setIsBackLoading(false)
    }
  }

  useEffect(() => {
    if (completeScreeningStep) {
      setStep(completeScreeningStep)
    }
  }, [completeScreeningStep])

  useEffect(() => {
    if (activeStep === ECompleteScreeningSteps.DRIVER_LICENSE) {
      if (formValue?.driverLicenseFrontPhotoUrl && formValue?.driverLicenseBackPhotoUrl) {
        typeof setIsDriverLicenses === 'function' && setIsDriverLicenses(true)
      }
      typeof setCompleteScreeningStep === 'function' && setCompleteScreeningStep(ECompleteScreeningSteps.DRIVER_LICENSE)
    }
  }, [activeStep])

  return (
    <CompleteScreeningContext.Provider
      value={{ nextStep, handleNextStep, handlePreviousStep, isDriverLicenses, setIsDriverLicenses }}
    >
      <Steps hidden={true} activeStep={activeStep} labelOrientation="vertical" responsive={false}>
        {steps.map(({ label, content }) => (
          <Step label={label} key={label}>
            <VStack
              spacing={0}
              textAlign="justify"
              background="white"
              height={{
                base: 'full',
                lg: completeScreeningStep === ECompleteScreeningSteps.DRIVER_LICENSE ? '100vh' : 'auto'
              }}
              justifyContent={{ base: 'space-between', lg: 'flex-start' }}
              width="full"
            >
              <VStack
                spacing={0}
                textAlign="justify"
                background="white"
                overflowY={{ base: 'auto', md: 'hidden' }}
                flexGrow={0.9}
                height={{ base: 'full', lg: 'auto' }}
                width="full"
                marginBottom={{ base: 6, md: 12, lg: 'auto' }}
              >
                {content}
              </VStack>

              <HStack
                width="full"
                maxWidth={{
                  base: 'full',
                  lg: DESKTOP_CONTAINER_WIDTH
                }}
                alignItems="space-between"
                justifyContent="space-between"
                spacing={2}
                paddingX={completeScreeningStep === ECompleteScreeningSteps.DRIVER_LICENSE && isDesktop ? 0 : 4}
                position={{
                  base: 'absolute',
                  md: 'relative'
                }}
                bottom={{
                  base: 6,
                  md: 12,
                  lg: 'auto'
                }}
                hidden={isUsingStepButton}
                paddingBottom={{ lg: 20 }}
              >
                <BottomBackButton isLoading={isBackLoading} onClick={handlePreviousStep} variant="solid">
                  Back
                </BottomBackButton>

                <BottomButton
                  colorScheme="teal"
                  variant="solid"
                  iconSpacing={2}
                  onClick={handleNextStep}
                  isDisabled={
                    completeScreeningStep === ECompleteScreeningSteps.DRIVER_LICENSE && !isDriverLicenses ? true : false
                  }
                  isLoading={isLoading}
                  rightIcon={<ArrowForwardIcon />}
                >
                  Continue
                </BottomButton>
              </HStack>
            </VStack>
          </Step>
        ))}
      </Steps>
    </CompleteScreeningContext.Provider>
  )
}
export default observer(CompleteScreening)
